import React, { useEffect, useMemo, useState } from 'react';
import '../../../styles/AdminDashboardPage.css';
import * as moment from 'moment';
import { Button } from 'reactstrap';
import SelectChips from '../../common/SelectChips';
import { useFetch } from '../../../hooks/useFetch';
import { AnalyticPropertyInfo, DashboardProperty,
    DashboardTourMonth,
    getAdminDashboardData, getAdminDashboardProperties,
    getAnalyticsExportData,
    getToursByMonthForDashboard, 
} from '../../../api/Property';
import { formatDate, momentDateFormat } from '../../../utilities/formatting';
import LoadingOverlay from '../../common/LoadingOverlay';
import { BorderedInfoCard } from './analyticsDashboardWidgets/borderedInfoCard';
import { ToursPie } from './analyticsDashboardWidgets/toursPie';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import { ToursDayCard } from './analyticsDashboardWidgets/toursDayCard';
import { ToursTimeCard } from './analyticsDashboardWidgets/toursTimeCard';
import { ToursTimeByDay } from './analyticsDashboardWidgets/toursTimeByDay';
import { ToursByMonth } from './analyticsDashboardWidgets/toursMonthCard';
import AnalyticsFilter from './analyticsDashboardWidgets/analyticsFilter';
import * as identity from '../../../utilities/identity';
import { Roles } from '../../../utilities/Enums';
import { exportAnalyticsData } from '../../../utilities/exportLog';

let exportIcon = require('../../../images/export_icon.png');


enum FilterType {
    Property,
    Company,
    City,
    State,
}

interface dropInterface {
    id: number;
    name: string;
    toolTip?: string;
}

export const AnalyticsDashboard = () => {

  const { data: properties, loading } = useFetch(getAdminDashboardProperties, [], [] as DashboardProperty[]);
  const [applicationWidgetStatus, setApplicationWidgetStatus] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<moment.Moment>(moment().subtract(30, 'days'));
  const [endDate, setEndDate] = useState<moment.Moment>(moment());
  const [tourByMonth, setTourByMonth] = useState<DashboardTourMonth[]>();

  const role = identity.getMyRole();

  const [selectedOption, setSelectedOption] = useState('');
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [selectedStateIds, setSelectedStateIds] = useState<number[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<number[]>([]);


  const [originalData, setOriginalData] = useState<AnalyticPropertyInfo>();
  const [loadedData, setIsLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [viewDialog, setViewDialog] = useState(false);
  
  const [loadedCompany, setLoadedCompany] = useState(false);
  const [loadedCity, setLoadedCity] = useState(false);
  const [loadedState, setLoadedState] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const [dashboardData, setDashboardData] = useState(originalData);
  const [screenWidth, setScreenWidth] = useState(0);
  const [loadingExport, setLoadingExport] = useState(false);

  const dateType = {
    lastMonth: 'Last 30 Days',
    monthToDate: 'Month To Date',
    lastSixMonths: 'Last 6 Months',
    lastThreeMonths: 'Last 3 Months',
    yearToDate: 'Year To Date',
  }

  // inorder to do this computation only once we are using useMemo
  const propertiesList: dropInterface[] = useMemo(()=>properties.map(({ propertyId, propertyName, goLiveDate }) => ({
        id: propertyId,
        name: propertyName,
        toolTip: formatDate(goLiveDate)
   })).sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
  }),[properties]);

  useEffect(() => {
    if (properties.length > 0) {
      const status = properties.some((property) => property.applicationWidgetStatus);
      setApplicationWidgetStatus(status);
    }
  }, [properties]);

  const companiesList: dropInterface[] = useMemo(() => properties.reduce<dropInterface[]>((uniqueCompanies, property) => {
    const existingCompany = uniqueCompanies.find(company => company.id === property.companyId);
    if (!existingCompany) {
        uniqueCompanies.push({ id: property.companyId, name: property.companyName });
    }
    return uniqueCompanies;
    }, []).sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
  }),[properties]);

  const citiesList : dropInterface[] = useMemo(() =>  properties.reduce<dropInterface[]>((uniqueCities, property) => {
    const existingCity = uniqueCities.find(city => city.name === property.city);
    if (!existingCity) {
        uniqueCities.push({ id: uniqueCities.length + 1, name: property.city });
    }
    return uniqueCities;
  }, []).sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
}),[properties]);

 const statesList : dropInterface[] = useMemo(() =>  properties.reduce<dropInterface[]>((uniqueStates, property) => {
    const existingState = uniqueStates.find(state => state.name === property.state);
    if (!existingState) {
      uniqueStates.push({ id: uniqueStates.length + 1, name: property.state });
    }
    return uniqueStates;
  }, []).sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
  }),[properties]);

  const getDashboardData = async () => {
            setIsLoading(true);
            const filterInfo = {
              propertyIds: selectedPropertyIds,
              isAllSelected: selectedPropertyIds.length === properties.length,
              fromDate: startDate.format(momentDateFormat),
              toDate: endDate.format(momentDateFormat)
            }
            let result = await getAdminDashboardData(filterInfo);
            if (result.success && result.data) {
                if(originalData === undefined){
                   setOriginalData(result.data);
                }
                setDashboardData(result.data);
                setErrorMessage('');
            } else {
                setErrorMessage(result.error);
            }

            let tourByMonthRes = await getToursByMonthForDashboard(filterInfo);
            if (tourByMonthRes.success && tourByMonthRes.data) {
                setTourByMonth(tourByMonthRes.data);
                setErrorMessage('');
            } else {
                setErrorMessage(result.error);
            }

            setIsLoading(false);
  };

  useEffect(()=>{
    getDashboardData()
  },[]);

  useEffect(() => {
    const handleResize = () => {
        setScreenWidth(window.innerWidth);
    };

    handleResize(); // for initial load
    window.addEventListener('resize', handleResize);

    return () => {
        window.removeEventListener('resize', handleResize);
    };
}, []);

useEffect(() => {
    if (properties.length > 0) {
        setSelectedPropertyIds(properties.map((property) => property.propertyId));
    }
}, [properties]);

useEffect(() => {
    if (companiesList.length > 0 && !loadedCompany) {
        setSelectedCompanyIds(companiesList.map((company) => company.id));
        setLoadedCompany(true);
    }
}, [companiesList]);

useEffect(() => {
    if (statesList.length > 0 && !loadedState) {
        setSelectedStateIds(statesList.map((state) => state.id));
        setLoadedState(true);
    }
}, [statesList]);

useEffect(() => {
    if (citiesList.length > 0 && !loadedCity) {
        setSelectedCityIds(citiesList.map((city) => city.id));
        setLoadedCity(true);
    }
}, [citiesList]);

  const resetData = () =>{
    addData();
    setSelectedOption('');
    setErrorMessage('');
    setStartDate(moment().subtract(30, 'days'));
    setEndDate(moment());
    setDashboardData(originalData);
  }

  const toggleAlert = () => setViewDialog(prevState => !prevState);

  const handleSelectedPropertyOption = (option: any, type: number, selectedIds: number[], setSelectedIds: any) => {
    const index = selectedIds.indexOf(option.id);
    if (index === -1) {
        setSelectedIds([...selectedIds, option.id]);
    } else {
        setSelectedIds(selectedIds.filter((id: number) => id !== option.id));
    }

    if(type === FilterType.Property){
        if(index !== -1){
            removeSync([option.id], type);
        }else{
            addSync([option.id], type);
        }

    } else if(type === FilterType.Company){
        if(index !== -1){
            // if we are removing a company property related to it also need to be removed
            const propertiesToRemove = properties.filter(property => property.companyId === option.id);
            const propertiesToRemoveIds = propertiesToRemove.map(property => property.propertyId);
            setSelectedPropertyIds(prevPropertyIds => prevPropertyIds.filter(id => !propertiesToRemoveIds.includes(id)));
            removeSync(propertiesToRemoveIds, type);
        } 
        else{
            const propertiesToAdd = properties.filter(property => property.companyId === option.id);
            const propertiesToAddIds = propertiesToAdd.map(property => property.propertyId);
            setSelectedPropertyIds(prevPropertyIds => [...prevPropertyIds, ...propertiesToAddIds]);
            addSync(propertiesToAddIds, type);
        }
    } else if (type === FilterType.City) {
        if (index !== -1) {
            // If we are removing a city property related to it, also need to be removed
            const propertiesToRemove = properties.filter(property => property.city === option.name);
            const propertiesToRemoveIds = propertiesToRemove.map(property => property.propertyId);
            setSelectedPropertyIds(prevPropertyIds => prevPropertyIds.filter(id => !propertiesToRemoveIds.includes(id)));
            removeSync(propertiesToRemoveIds, type);
        } else {
            // If we are adding a city, add the properties related to it
            const propertiesToAdd = properties.filter(property => property.city === option.name);
            const propertiesToAddIds = propertiesToAdd.map(property => property.propertyId);
            setSelectedPropertyIds(prevPropertyIds => [...prevPropertyIds, ...propertiesToAddIds]);
            addSync(propertiesToAddIds, type);
        }
    } else if (type === FilterType.State) {
        if (index !== -1) {
            // If we are removing a state property related to it, also need to be removed
            const propertiesToRemove = properties.filter(property => property.state === option.name);
            const propertiesToRemoveIds = propertiesToRemove.map(property => property.propertyId);
            setSelectedPropertyIds(prevPropertyIds => prevPropertyIds.filter(id => !propertiesToRemoveIds.includes(id)));
            removeSync(propertiesToRemoveIds, type);
        } else {
            // If we are adding a state, add the properties related to it
            const propertiesToAdd = properties.filter(property => property.state === option.name);
            const propertiesToAddIds = propertiesToAdd.map(property => property.propertyId);
            setSelectedPropertyIds(prevPropertyIds => [...prevPropertyIds, ...propertiesToAddIds]);
            // add information for that particular id - add state, city, country
            addSync(propertiesToAddIds, type);
        }
    }
   
};

const addSync = (propertyIds: number[], from: FilterType) =>{
    propertyIds.forEach((propertyId)=>{
       const property = properties.find(property=>property.propertyId === propertyId)
       // basically adding company if it does not exist, if we are calling this function from company, it means already selected so in order to ignore kept this if condition
       if(from!== FilterType.Company && !selectedCompanyIds.includes(property!.companyId)){
          setSelectedCompanyIds(prevSelectedCompanyIds => [...prevSelectedCompanyIds, property!.companyId]);
       }

       const city = citiesList.find(city=>city.name === property!.city)
       if(from!== FilterType.City && !selectedCityIds.includes(city!.id)){
          setSelectedCityIds(prevSelectedCityIds => [...prevSelectedCityIds, city!.id]);
       }

       const state = statesList.find(state=>state.name === property!.state)
       if(from!== FilterType.State && !selectedStateIds.includes(state!.id)){
          setSelectedStateIds(prevSelectedStateIds => [...prevSelectedStateIds, state!.id]);
       }
    })
}

const removeSync = (propertyIdsToRemove: number[], from: FilterType) =>{
    propertyIdsToRemove.forEach((propertyId)=>{
       const property = properties.find(property=>property.propertyId === propertyId);

       if(from!== FilterType.Company && selectedCompanyIds.includes(property!.companyId)){
         // second case that needs to be done - if other selected property has this company we should not remove it 
            const propertiesL = selectedPropertyIds.filter((propertyId) => {
                const sProperty = properties.find(p => p.propertyId === propertyId);
                return sProperty ? sProperty.companyId === property!.companyId : false;
            });
            // If no other properties with the same company ID are found, remove the company ID from selectedCompanyIds ( basically if it is more than 1 then we should not remove it)
            if (propertiesL.length === 1) {
                setSelectedCompanyIds(prevCompanyIds => prevCompanyIds.filter(id => id !== property!.companyId));
            }
       }

       const city = citiesList.find(city=>city.name === property!.city);
       if(from!== FilterType.City && selectedCityIds.includes(city!.id)){
           const propertiesL = selectedPropertyIds.filter((propertyId) => {
               const sProperty = properties.find(p => p.propertyId === propertyId);
               return sProperty ? sProperty.city === property!.city : false;
           });
           if (propertiesL.length === 1) {
               setSelectedCityIds(prevCityIds => prevCityIds.filter(id => id !== city!.id));
           }
      }

      const state = statesList.find(state=>state.name === property!.state);
       if(from!== FilterType.State && selectedStateIds.includes(state!.id)){ 
           const propertiesL = selectedPropertyIds.filter((propertyId) => {
               const sProperty = properties.find(p => p.propertyId === propertyId);
               return sProperty ? sProperty.state === property!.state : false;
           });
           if (propertiesL.length === 1) {
               setSelectedStateIds(prevStateIds => prevStateIds.filter(id => id !== state!.id));
           }
      }

    })
}

const handleExportAllData = async () => {
    setLoadingExport(true);
    const filterInfo = {
        propertyIds: selectedPropertyIds,
        isAllSelected: selectedPropertyIds.length === properties.length,
        fromDate: startDate.format(momentDateFormat),
        toDate: endDate.format(momentDateFormat)
    }

    await getAnalyticsExportData(filterInfo).then((r:any) => {
        if (r.data !== undefined) {
            exportAnalyticsData(r.data);
        }
    });
    setLoadingExport(false);
};

const clearData=()=>{
    setSelectedPropertyIds([]);
    setSelectedCityIds([]);
    setSelectedCompanyIds([]);
    setSelectedStateIds([]);
}

const addData=()=>{
    setSelectedPropertyIds(properties.map((property)=>property.propertyId));
    setSelectedCompanyIds(companiesList.map((company) => company.id));
    setSelectedStateIds(statesList.map((state) => state.id));
    setSelectedCityIds(citiesList.map((city) => city.id));
}

   

  return (
     <>
        <LoadingOverlay show={(loading || loadedData)} inBootstrapColumn={true} />
        {errorMessage && <p className="text-danger">{errorMessage}</p>}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', marginTop: '12px' }}>
            <div>
                <AnalyticsFilter
                    showOnDevice={screenWidth>0 && screenWidth>576}
                    resetData={resetData}
                    getDashboardData={getDashboardData}
                    setAlertMessage={setAlertMessage}
                    setViewDialog={setViewDialog}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    dateType={dateType}
                    dPropertyList={propertiesList}
                    dCompaniesList={companiesList}
                    dCityList={citiesList}
                    dStatesList={statesList}
                    selectedPropertyIds={selectedPropertyIds}
                    selectedCompanyIds={selectedCompanyIds}
                    selectedCityIds={selectedCityIds}
                    selectedStateIds={selectedStateIds}
                    setSelectedPropertyIds={setSelectedPropertyIds}
                    setSelectedCompanyIds={setSelectedCompanyIds}
                    setSelectedCityIds={setSelectedCityIds}
                    setSelectedStateIds={setSelectedStateIds}
                    handleSelectedPropertyOption={handleSelectedPropertyOption}
                    clearAll={clearData}
                    addAll={addData}
                />
            </div>
         <div>
            <div>
                <AnalyticsFilter
                    showOnDevice={screenWidth>0 && screenWidth<576}
                    resetData={resetData}
                    getDashboardData={getDashboardData}
                    setAlertMessage={setAlertMessage}
                    setViewDialog={setViewDialog}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    dateType={dateType}
                    dPropertyList={propertiesList}
                    dCompaniesList={companiesList}
                    dCityList={citiesList}
                    dStatesList={statesList}
                    selectedPropertyIds={selectedPropertyIds}
                    selectedCompanyIds={selectedCompanyIds}
                    selectedCityIds={selectedCityIds}
                    selectedStateIds={selectedStateIds}
                    setSelectedPropertyIds={setSelectedPropertyIds}
                    setSelectedCompanyIds={setSelectedCompanyIds}
                    setSelectedCityIds={setSelectedCityIds}
                    setSelectedStateIds={setSelectedStateIds}
                    handleSelectedPropertyOption={handleSelectedPropertyOption}
                    clearAll={clearData}
                    addAll={addData}
                />
            </div>
            <div className='row' style={{ justifyContent: 'space-between', gap: '12px', marginLeft: '12px', marginRight: '24px' }}>
                <div className='row' style={{ justifyContent: 'space-between' }}>
                       <div className='row' style={{ marginRight: '24px', flexGrow: 1, marginLeft: '0px', minHeight: '38px' }}>
                       <SelectChips  
                            heading = {"Properties"}
                            listItems = {propertiesList}
                            type={FilterType.Property}
                            selectedIds = {selectedPropertyIds}
                            setSelectedIds = {setSelectedPropertyIds}
                            handleSelectedPropertyOption={handleSelectedPropertyOption}
                            clearAll={()=>clearData()}
                        />
                        <SelectChips 
                            heading = {"Companies"}
                            listItems = {companiesList}
                            type={FilterType.Company}
                            selectedIds = {selectedCompanyIds}
                            setSelectedIds = {setSelectedCompanyIds}
                            handleSelectedPropertyOption={handleSelectedPropertyOption}
                            clearAll={()=>clearData()}
                        />
                        <SelectChips 
                            heading = {"Cities"}
                            listItems = {citiesList}
                            type={FilterType.City}
                            selectedIds = {selectedCityIds}
                            setSelectedIds = {setSelectedCityIds}
                            handleSelectedPropertyOption={handleSelectedPropertyOption}
                            clearAll={()=>clearData()}
                        />
                        <SelectChips 
                            heading = {"States"}
                            listItems = {statesList}
                            type={FilterType.State}
                            selectedIds = {selectedStateIds}
                            setSelectedIds = {setSelectedStateIds}
                            handleSelectedPropertyOption={handleSelectedPropertyOption}
                            clearAll={()=>clearData()}
                        />
                       </div>
                </div>
                {(identity.userIsCompanyAdmin() || identity.userIsSystemAdmin()) && (
                            <div>
                                {loadingExport ? (
                                    <label className="button-disabled">Loading Export...</label>
                                ) : (
                                   <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                                       <label
                                            className="button-available"
                                            style={{ fontWeight: 'bold', color: '#33ADFF' }}
                                            onClick={() => handleExportAllData()}
                                        >
                                            Export Data For All Properties
                                        </label>
                                        <img style={{ height: '20px', width: '20px' }} src={exportIcon} />
                                   </div>
                                )}
                            </div>
                        )}
            </div>
            <div className='row col' style={{ padding: '12px', gap: '12px', paddingTop: '0px' }}>
                    <BorderedInfoCard
                        heading="Unique Prospects"
                        biggerTitle={true}
                        title={dashboardData ? dashboardData.dashboardCards.uniqueProspects : 0}
                        subText1="Unique Guest Cards"
                    />
                    <BorderedInfoCard
                        heading="Scheduled Tours"
                        biggerTitle={true}
                        title={dashboardData ? dashboardData.dashboardCards.scheduleTours : 0}
                    />
                    <BorderedInfoCard
                        heading="Completed Tours"
                        title={dashboardData ? dashboardData.dashboardCards.completedTours : 0}
                        subText1="Actual tours"
                        showSecondContainer={true}
                        percentage={dashboardData ? dashboardData.dashboardCards.completedToursPercentage : 0}
                        subText2={"% Scheduled"}
                        breakSubText={"that toured"}
                    />
                   { (identity.userIsSystemAdmin() || (role === Roles.CompanyAdmin && applicationWidgetStatus))  && <BorderedInfoCard
                        heading="Applications"
                        title={dashboardData ? dashboardData.dashboardCards.applicationSubmitted : 0}
                        subText1="Applied to Lease"
                        showSecondContainer={true}
                        percentage={dashboardData ? dashboardData.dashboardCards.applicationSubmittedPercentage: 0}
                        subText2={"% Completed"}
                        breakSubText={"that applied"}
                    /> }
                    <ToursPie 
                        heading={'Tours Outside 9-5 M-F Business Hours'}
                        value1={dashboardData?dashboardData.dashboardCards.businessHourScheduledPercentage:0}
                        value2={dashboardData?dashboardData.dashboardCards.nonBusinessHourScheduledPercentage:0}
                        title1Key={'During'}
                        title1Value={dashboardData? `${dashboardData.dashboardCards.businessHourScheduled.toString()}`:''} 
                        title2Key={'Outside'} 
                        title2Value={dashboardData? `${dashboardData.dashboardCards.nonBusinessHourScheduled.toString()}`:''}
                    />
                    <BorderedInfoCard
                        heading="Tours Scheduled"
                        title={dashboardData ? dashboardData.dashboardCards.tourScheduledSameDayPercentage : 0}
                        showTitleWithPercentage={true}
                        subText1="Same Day"
                        showSecondContainer={true}
                        percentage={dashboardData ? dashboardData.dashboardCards.tourScheduledSameHourPercentage : 0}
                        subText2={"Within 1 hr"}
                    />
                    <ToursPie 
                        heading={'Weekdays vs Weekend Tours'}
                        value1={dashboardData?dashboardData.dashboardCards.weekDaysScheduledPercentage:0}
                        value2={dashboardData?dashboardData.dashboardCards.weekEndScheduledPercentage:0}
                        title1Key={'Weekdays'}
                        title1Value={dashboardData? `${dashboardData.dashboardCards.weekDaysScheduled.toString()}`:''} 
                        title2Key={'Weekends'} 
                        title2Value={dashboardData? `${dashboardData.dashboardCards.weekEndScheduled.toString()}`:''}
                    />
                    <ToursDayCard dashboardData={dashboardData}/>
                    <ToursTimeCard dashboardData={dashboardData}/>
                    <ToursTimeByDay dashboardData={dashboardData} />
                    {tourByMonth && <ToursByMonth tourByMonth={tourByMonth}/>}
            </div>
         </div>
        </div>
        <Modal isOpen={viewDialog} onClose={toggleAlert}>
            <ModalBody>
                <div>
                    <p>
                        {alertMessage}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button addlClassName="btn-primary" onClick={toggleAlert} >Close</Button>
            </ModalFooter>
        </Modal>
     </>
  )
}



// WEBPACK FOOTER //
// ./src/components/pages/admin/AnalyticsDashboard.tsx